import { stripHtml } from 'core/utils/utils';

import type { ContentLocationItem, KcActivitiesByDayItem, KcActivityItem, RadarSeverity } from '../types/radar';

type Locatable = {
  lesson: { name: string } | null;
  unit: { name: string };
};

/**
 * Derive a radar severity bucket from an error rate in [0..1].
 * Thresholds are shared across the radar list and alert detail views so
 * severity stays consistent end-to-end.
 */
export function getSeverityFromErrorRate(errorRate: number): RadarSeverity {
  if (errorRate > 0.6) return 'high';
  if (errorRate >= 0.4) return 'medium';
  return 'low';
}

/**
 * Format an array of annotation locations into readable strings of the form
 * "Unit – Lesson", stripping HTML and ignoring lessons without a name.
 */
export function formatLocations(locations: Locatable[]): string[] {
  return locations
    .map((loc) => {
      const parts = [stripHtml(loc.unit.name)].filter(Boolean);
      if (loc.lesson) {
        const lessonName = stripHtml(loc.lesson.name);
        if (lessonName) {
          parts.push(lessonName);
        }
      }
      return parts.join(' \u2013 ');
    })
    .filter(Boolean);
}

/**
 * Format an array of ContentLocationItem (response of /stats/users/:userId/kcs/:kcId/progress)
 * into readable strings of the form "Unit \u2013 Lesson", stripping HTML and ignoring lessons
 * without a name. Equivalent to {@link formatLocations} but keeps the API explicit so callers
 * targeting the KC progress endpoint can rely on a typed entry point.
 */
export function formatContentLocations(items: ContentLocationItem[]): string[] {
  return formatLocations(items);
}

/**
 * Group a flat list of KC activities by the calendar day of `respondedAt` (UTC `YYYY-MM-DD`).
 * Preserves the input order, so when the source is sorted DESC by `respondedAt` the resulting
 * groups are day-DESC and within-day DESC for free — no extra sort needed.
 */
export function groupActivitiesByDay(activities: KcActivityItem[]): KcActivitiesByDayItem[] {
  const days: KcActivitiesByDayItem[] = [];
  for (const activity of activities) {
    const date = activity.respondedAt.slice(0, 10);
    const lastDay = days[days.length - 1];
    if (lastDay?.date === date) {
      lastDay.activities.push(activity);
    } else {
      days.push({ date, activities: [activity] });
    }
  }
  return days;
}
